import {layout} from './layout.js';

const finitePoint=p=>p&&Number.isFinite(p.x)&&Number.isFinite(p.z);
const solids=layout.solids.filter(s=>s.id!=='open-door');
// Walking uses a simple cabinet envelope. Sight/picking must instead respect
// its five actual shell slabs, otherwise the open mounting slot occludes itself.
const visibilitySolids=solids.flatMap(s=>s.id==='cabinet'?[
 {...s,maxZ:s.minZ+.014},{...s,maxX:s.minX+.014},{...s,minX:s.maxX-.014},
 {...s,height:.014},{...s,minY:s.minY+s.height-.014,height:.014},
]:[s]);
const leaf={minX:0,maxX:layout.door.width,minZ:-layout.door.thickness/2,maxZ:layout.door.thickness/2,minY:0,height:layout.door.height};
let doorCos=Math.cos(layout.door.openAngle),doorSin=Math.sin(layout.door.openAngle);
// Internal world/navigation bridge: the four public movement signatures remain
// unchanged. A lone navigation module starts with the traversable final pose;
// buildMissionWorld/update supplies the exact rendered hinge angle each frame.
export function setDoorAngle(angle){
 if(!Number.isFinite(angle))return;
 doorCos=Math.cos(angle);doorSin=Math.sin(angle);
}
function leafPoint(x,z){
 const dx=x-layout.door.hingeX,dz=z-layout.door.z;
 return {x:doorCos*dx-doorSin*dz,z:doorSin*dx+doorCos*dz};
}
function overlapsCircle(x,z,r,s){
 const dx=x-Math.max(s.minX,Math.min(s.maxX,x));
 const dz=z-Math.max(s.minZ,Math.min(s.maxZ,z));
 return dx*dx+dz*dz<r*r-1e-12;
}
export function canOccupy(x,z){
 if(!Number.isFinite(x)||!Number.isFinite(z))return false;
 const b=layout.bounds,r=layout.radius;
 if(x<b.minX+r||x>b.maxX-r||z<b.minZ+r||z>b.maxZ-r)return false;
 const local=leafPoint(x,z);
 if(overlapsCircle(local.x,local.z,r,leaf))return false;
 return !solids.some(s=>s.minY<layout.eyeHeight&&s.minY+s.height>0&&overlapsCircle(x,z,r,s));
}
export function groundHeight(){return 0;}
export function movePlayer(position,delta){
 const out={...position};
 if(!finitePoint(position)||!finitePoint(delta))return out;
 // Sweep with bounded substeps, then slide separately on each axis. Prevents
 // tunnelling even when a tab wakes with a large requested displacement.
 const steps=Math.max(1,Math.ceil(Math.hypot(delta.x,delta.z)/.08));
 if(steps>10000)return out;
 const dx=delta.x/steps,dz=delta.z/steps;
 for(let i=0;i<steps;i++){
  if(canOccupy(out.x+dx,out.z+dz)){out.x+=dx;out.z+=dz;}
  else{if(canOccupy(out.x+dx,out.z))out.x+=dx;if(canOccupy(out.x,out.z+dz))out.z+=dz;}
 }
 return out;
}
function intersects(a,b,ay,by,s){
 let lo=1e-5,hi=1-1e-5;
 for(const [start,diff,min,max] of [[a.x,b.x-a.x,s.minX,s.maxX],[ay,by-ay,s.minY,s.minY+s.height],[a.z,b.z-a.z,s.minZ,s.maxZ]]){
  if(Math.abs(diff)<1e-12){if(start<min||start>max)return false;}
  else{const p=(min-start)/diff,q=(max-start)/diff;lo=Math.max(lo,Math.min(p,q));hi=Math.min(hi,Math.max(p,q));if(lo>hi)return false;}
 }
 return lo<=hi;
}
export function lineClear(a,b){
 if(!finitePoint(a)||!finitePoint(b))return false;
 const ay=a.y??layout.eyeHeight,by=b.y??layout.eyeHeight;
 if(!Number.isFinite(ay)||!Number.isFinite(by))return false;
 // Exact segment/AABB slabs, including the door in its rotating local frame.
 if(intersects(leafPoint(a.x,a.z),leafPoint(b.x,b.z),ay,by,leaf))return false;
 return !visibilitySolids.some(s=>intersects(a,b,ay,by,s));
}
